import { z } from 'zod';
import {
  baseQueryArgs,
  baseExtraOptions,
  rsosApi,
} from '@rsos/reduxDataStore/src/features/rsosApi';
import {
  PreparedResponseType,
  SINGLETON_RESPONSE_TYPES,
} from './preconfiguredMessagesTypes';
import { isAuthorityMessage } from './preconfiguredMessagesUtils';

const PreconfiguredResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  type: z.nativeEnum(PreparedResponseType).nullable(),
  enabled: z.boolean(),
  created_at: z.number(),
  modified_at: z.number(),
  owner: z.string(),
});

const ApiResponseSchema = z.object({
  preconfigured_responses: z.array(PreconfiguredResponseSchema),
});

export type PreconfiguredResponse = z.infer<typeof PreconfiguredResponseSchema>;

const PreconfiguredMessagesTags = {
  Messages: 'PreconfiguredMessages',
} as const;

const enhancedApi = rsosApi.enhanceEndpoints({
  addTagTypes: Object.values(PreconfiguredMessagesTags),
});

/**
 * RTK Query endpoints for Preconfigured Messages (CRUD).
 * Backed by the tf911 service's /v1/tf911/psap/{org}/preconfigured-responses routes.
 *
 * All responses are validated with Zod in transformResponse. Tag invalidation
 * keeps the message list in sync after mutations.
 */
export const preconfiguredMessagesApi = enhancedApi.injectEndpoints({
  endpoints: builder => ({
    getPreconfiguredMessages: builder.query({
      query: ({ org, enabled }: { org: string; enabled?: boolean }) => ({
        ...baseQueryArgs,
        url: `v1/tf911/psap/${org}/preconfigured-responses`,
        method: 'GET',
        params: enabled !== undefined ? { enabled } : undefined,
      }),
      transformResponse: (response: unknown) => {
        return ApiResponseSchema.parse(response).preconfigured_responses;
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: PreconfiguredMessagesTags.Messages,
                id,
              })),
              PreconfiguredMessagesTags.Messages,
            ]
          : [PreconfiguredMessagesTags.Messages],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    createPreconfiguredMessage: builder.mutation({
      query: ({
        org,
        message,
      }: {
        org: string;
        message: Partial<PreconfiguredResponse>;
      }) => ({
        ...baseQueryArgs,
        url: `v1/tf911/psap/${org}/preconfigured-responses`,
        method: 'POST',
        body: message,
      }),
      transformResponse: (response: unknown) => {
        return PreconfiguredResponseSchema.parse(response);
      },
      invalidatesTags: [PreconfiguredMessagesTags.Messages],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    updatePreconfiguredMessage: builder.mutation({
      query: ({
        org,
        id,
        updates,
      }: {
        org: string;
        id: string;
        updates: Partial<PreconfiguredResponse>;
      }) => ({
        ...baseQueryArgs,
        url: `v1/tf911/psap/${org}/preconfigured-responses/${id}`,
        method: 'PATCH',
        body: updates,
      }),
      transformResponse: (response: unknown) => {
        return PreconfiguredResponseSchema.parse(response);
      },
      // Invalidating a tag re-fetches the entire list. Instead, we wait for
      // the server response and patch only the affected item in the cache.
      onQueryStarted: async (
        { org, id, updates },
        { dispatch, queryFulfilled },
      ) => {
        let updatedMessage;
        try {
          const result = await queryFulfilled;
          updatedMessage = result.data;
        } catch {
          // queryFulfilled rejected (HTTP error, network failure, or schema mismatch).
          // RTK Query surfaces the error to the caller via the mutation result — no
          // cache update needed here.
          return;
        }

        dispatch(
          preconfiguredMessagesApi.util.updateQueryData(
            'getPreconfiguredMessages',
            { org },
            draft => {
              // When assigning a singleton type (ending, location request,
              // roadside assistance), the backend demotes whichever
              // authority-owned message previously held that same type back to
              // `custom`. It never touches the rapidsos system default, so we
              // skip those here too — otherwise the cache would lose the system
              // fallback. Mirror the backend exactly (demote to `custom`, not
              // null) so the cache matches the eventual refetch.
              if (
                updates.type &&
                SINGLETON_RESPONSE_TYPES.includes(updates.type)
              ) {
                draft.forEach(m => {
                  if (
                    m.type === updates.type &&
                    m.id !== id &&
                    isAuthorityMessage(m)
                  ) {
                    m.type = PreparedResponseType.custom;
                  }
                });
              }
              const index = draft.findIndex(m => m.id === id);
              if (index !== -1) {
                draft[index] = updatedMessage;
              }
            },
          ),
        );
      },
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
    deletePreconfiguredMessage: builder.mutation({
      query: ({ org, id }: { org: string; id: string }) => ({
        ...baseQueryArgs,
        url: `v1/tf911/psap/${org}/preconfigured-responses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [PreconfiguredMessagesTags.Messages],
      extraOptions: {
        ...baseExtraOptions,
      },
    }),
  }),
  overrideExisting: 'throw',
});

export const {
  useGetPreconfiguredMessagesQuery,
  useLazyGetPreconfiguredMessagesQuery,
  useCreatePreconfiguredMessageMutation,
  useUpdatePreconfiguredMessageMutation,
  useDeletePreconfiguredMessageMutation,
} = preconfiguredMessagesApi;
